import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

export interface PaginationProps {
  totalPages: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  className?: string;
  variant?: 'light' | 'dark';
}

export default function Pagination({
  totalPages,
  currentPage,
  onPageChange,
  className = '',
  variant = 'light',
}: PaginationProps) {
  if (totalPages <= 1) return null;

  const getPageRange = () => {
    const maxDisplayedPages = 5;
    if (totalPages <= maxDisplayedPages + 2) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    let startPage = Math.max(currentPage - Math.floor(maxDisplayedPages / 2), 1);
    let endPage = startPage + maxDisplayedPages - 1;

    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = Math.max(endPage - maxDisplayedPages + 1, 1);
    }

    const pages = [];

    if (startPage > 1) {
      pages.push(1);
      if (startPage > 2) {
        pages.push('ellipsis-start');
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push('ellipsis-end');
      }
      pages.push(totalPages);
    }

    return pages;
  };

  const pages = getPageRange();

  const getButtonStyles = (isDisabled: boolean, isActive: boolean = false) => {
    if (variant === 'dark') {
      if (isActive) {
        return 'bg-[var(--anl-accent)] text-[var(--anl-text)] font-medium';
      }
      if (isDisabled) {
        return 'text-[var(--anl-text-muted)] cursor-not-allowed';
      }
      return 'text-[var(--anl-text-muted)] hover:text-[var(--anl-text)] hover:bg-[var(--anl-surface-elev)]';
    } else {
      if (isActive) {
        return 'bg-blue-500 text-white font-medium';
      }
      if (isDisabled) {
        return 'text-gray-300 cursor-not-allowed';
      }
      return 'text-gray-700 hover:bg-gray-100';
    }
  };

  const getPageButtonStyles = (isActive: boolean) => {
    if (variant === 'dark') {
      return isActive
        ? 'bg-[var(--anl-accent)] text-[var(--anl-text)] font-medium'
        : 'text-[var(--anl-text-muted)] hover:text-[var(--anl-text)] hover:bg-[var(--anl-surface-elev)] border border-[var(--anl-border)]';
    } else {
      return isActive ? 'bg-blue-500 text-white font-medium' : 'text-gray-700 hover:bg-gray-100';
    }
  };

  const getEllipsisStyles = () => {
    return variant === 'dark' ? 'text-[var(--anl-text-muted)]' : 'text-gray-400';
  };

  return (
    <div
      className={`flex items-center justify-center gap-1.5 ${variant === 'dark' ? 'border-t border-[var(--anl-border)]' : 'border-t'} p-4 ${className}`}
    >
      <button
        onClick={() => onPageChange(1)}
        disabled={currentPage === 1}
        className={`rounded-md p-1.5 ${getButtonStyles(currentPage === 1)}`}
        aria-label="Go to first page"
      >
        <ChevronsLeft size={18} />
      </button>

      <button
        onClick={() => onPageChange(Math.max(1, currentPage - 1))}
        disabled={currentPage === 1}
        className={`rounded-md p-1.5 ${getButtonStyles(currentPage === 1)}`}
        aria-label="Go to previous page"
      >
        <ChevronLeft size={18} />
      </button>

      <div className="flex gap-1">
        {pages.map((page, index) =>
          typeof page === 'number' ? (
            <button
              key={`page-${page}`}
              onClick={() => onPageChange(page)}
              className={`flex h-8 min-w-[2rem] items-center justify-center rounded-md transition-colors ${getPageButtonStyles(currentPage === page)}`}
            >
              {page}
            </button>
          ) : (
            <span
              key={`${page}-${index}`}
              className={`flex items-center px-1 ${getEllipsisStyles()}`}
            >
              •••
            </span>
          ),
        )}
      </div>

      <button
        onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
        disabled={currentPage === totalPages}
        className={`rounded-md p-1.5 ${getButtonStyles(currentPage === totalPages)}`}
        aria-label="Go to next page"
      >
        <ChevronRight size={18} />
      </button>

      <button
        onClick={() => onPageChange(totalPages)}
        disabled={currentPage === totalPages}
        className={`rounded-md p-1.5 ${getButtonStyles(currentPage === totalPages)}`}
        aria-label="Go to last page"
      >
        <ChevronsRight size={18} />
      </button>
    </div>
  );
}
